import React, { useMemo, useState, useCallback } from 'react';
import {
  IconButton,
  Typography,
  useMediaQuery,
  Grid,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  Paper,
} from '@mui/material';
import COLORS from '@/resources/style/Themes';
import PageHeader from '@/components/utils/PageHeader';
import LightTooltip from '@/components/utils/LightTooltip';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '@/resources/strings/StringKeys';
import {
  Add,
  Delete,
  Edit,
  LockOutlined,
  MoreVert,
  Visibility,
} from '@mui/icons-material';
import type { PvdConfigurationOnAzure } from '@/../generated-client/models';
import { GridColDef } from '@mui/x-data-grid';
import { NavigatorDataGrid } from '@/components/data/NavigatorDataGrid';
import { PvdSensitivityQuickViewModal } from './modals/PvdSensitivityQuickViewModal';
import {
  ENTITLEMENT_LOCKED_ROW_CLASS,
  entitlementLockedRowSx,
  entitlementLockedCardSx,
  isRowEntitlementLocked,
} from '@/utils/entitlements/lockedRowStyle';

type PvdConfig = PvdConfigurationOnAzure;

/**
 * Sub-component to handle independent hover state for sensitivity cells
 * to avoid Hook violations in DataGrid renderCell.
 */
const PvdSensitivityCell = ({
  row,
  value,
  type,
  onShowModal,
  translateConfigValue,
}: {
  row: PvdConfig;
  value: string;
  type: 'voltage' | 'current';
  onShowModal: (config: PvdConfig, type: 'voltage' | 'current') => void;
  translateConfigValue: (val: string) => string;
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Box
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        height: '100%',
        width: '100%',
      }}
    >
      <Typography sx={{ fontSize: 14 }}>{translateConfigValue(value)}</Typography>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onShowModal(row, type);
        }}
        sx={{
          color: COLORS.SAFEGUARD_BLUE,
          opacity: isHovered ? 1 : 0,
          transition: 'opacity 0.2s',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            opacity: 1,
          },
        }}
      >
        <Visibility sx={{ fontSize: '1.2rem' }} />
      </IconButton>
    </Box>
  );
};

const summarizeVoltage = (row: PvdConfig): string => {
  const anyChip = row.voltage_allowed_settings.some(Boolean);
  if (!anyChip && !row.adaptive_voltage_allowed) return 'Disabled';
  if (row.voltage_allowed_settings.every(Boolean) && row.adaptive_voltage_allowed) return 'Enabled';
  return 'Custom';
};

const summarizeCurrent = (row: PvdConfig): string => {
  const anyChip = row.current_allowed_settings.some(Boolean);
  if (!anyChip && !row.adaptive_current_allowed) return 'Disabled';
  if (row.current_allowed_settings.every(Boolean) && row.adaptive_current_allowed) return 'Enabled';
  return 'Custom';
};

interface Props {
  setShowCreateConfigurationModal: () => void;
  editConfiguration: (config: PvdConfig) => void;
  showConfigurationToEditModal: () => void;
  setConfirmMessage: (m: string) => void;
  setConfirmTitle: (t: string) => void;
  setShowConfirmModal: (s: boolean) => void;
  setConfigurationToRemove: (c: PvdConfig) => void;
  configToHighlight: string | null;
  specificationResponseData: PvdConfig[];
  rowsLocked: boolean;
}

const PvdConfigTable: React.FC<Props> = ({
  setShowCreateConfigurationModal,
  editConfiguration,
  showConfigurationToEditModal,
  setConfirmMessage,
  setConfirmTitle,
  setShowConfirmModal,
  setConfigurationToRemove,
  configToHighlight,
  specificationResponseData,
  rowsLocked,
}) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width:600px)');
  const [menuState, setMenuState] = useState<{
    anchorEl: HTMLElement | null;
    activeSpec: string | null;
  }>({
    anchorEl: null,
    activeSpec: null,
  });

  const [sensitivityModal, setSensitivityModal] = useState<{
    show: boolean;
    config: PvdConfig | null;
    type: 'voltage' | 'current' | null;
  }>({ show: false, config: null, type: null });

  const handleCloseSensitivityModal = useCallback(() => {
    setSensitivityModal({ show: false, config: null, type: null });
  }, []);

  const sortedConfigurations = useMemo(
    () =>
      [...specificationResponseData].sort((a, b) => {
        const rank = (row: PvdConfig) => {
          if (row.is_base_config) return 0;
          if (row.spec_name === 'Default') return 1;
          return 2;
        };
        const rankDiff = rank(a) - rank(b);
        if (rankDiff !== 0) return rankDiff;
        return a.spec_name.localeCompare(b.spec_name);
      }),
    [specificationResponseData],
  );

  const translateConfigValue = useCallback(
    (value: string) => {
      const dict: Record<string, string> = {
        Disabled: t(StringKeys.DISABLED),
        Enabled: t(StringKeys.ENABLED),
        Custom: t(StringKeys.CUSTOM),
      };
      return dict[value] ?? value;
    },
    [t],
  );

  const columns = useMemo<GridColDef<PvdConfig>[]>(
    () => [
      {
        field: 'spec_name',
        headerName: t(StringKeys.CONFIGURATION_NAME),
        minWidth: 175,
        flex: 1,
        display: 'flex',
        align: 'left',
        headerAlign: 'left',
        renderCell: ({ row }) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              height: '100%',
              pl: 3,
            }}
          >
            <Typography sx={{ fontWeight: 500, fontSize: 14 }}>
              {row.spec_name}
            </Typography>
            {row.is_base_config && (
              <LightTooltip title={t(StringKeys.BASE_CONFIG_LOCKED_INFO)}>
                <IconButton
                  size="small"
                  onClick={(e) => e.stopPropagation()}
                  aria-label={t(StringKeys.BASE_CONFIG_LOCKED_INFO)}
                  sx={{ p: 0.25 }}
                >
                  <LockOutlined
                    sx={{ fontSize: 16, color: COLORS.RED }}
                  />
                </IconButton>
              </LightTooltip>
            )}
          </Box>
        ),
      },
      {
        field: 'voltage_sensitivity',
        headerName: t(StringKeys.VOLTAGE_SENSITIVITY),
        minWidth: 175,
        flex: 0.5,
        display: 'flex',
        align: 'left',
        headerAlign: 'left',
        valueGetter: (_, row) => summarizeVoltage(row),
        renderCell: ({ row, value }) => (
          <PvdSensitivityCell
            row={row}
            value={value}
            type="voltage"
            translateConfigValue={translateConfigValue}
            onShowModal={(config, type) =>
              setSensitivityModal({ show: true, config, type })
            }
          />
        ),
      },
      {
        field: 'current_sensitivity',
        headerName: t(StringKeys.CURRENT_SENSITIVITY),
        minWidth: 175,
        flex: 0.5,
        display: 'flex',
        align: 'left',
        headerAlign: 'left',
        valueGetter: (_, row) => summarizeCurrent(row),
        renderCell: ({ row, value }) => (
          <PvdSensitivityCell
            row={row}
            value={value}
            type="current"
            translateConfigValue={translateConfigValue}
            onShowModal={(config, type) =>
              setSensitivityModal({ show: true, config, type })
            }
          />
        ),
      },
      {
        field: 'edit',
        headerName: t(StringKeys.EDIT),
        minWidth: 100,
        flex: 0.25,
        display: 'flex',
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          if (row.is_base_config) return null;
          return (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                editConfiguration(row);
                showConfigurationToEditModal();
              }}
              sx={{
                backgroundColor: COLORS.LIGHT_GREEN,
                borderRadius: '10px',
                width: 40,
                height: 40,
                '&:hover': { backgroundColor: COLORS.HOVER_GREEN },
              }}
            >
              <Edit sx={{ color: COLORS.DARK_GREEN }} />
            </IconButton>
          );
        },
      },
      {
        field: 'delete',
        headerName: t(StringKeys.DELETE),
        minWidth: 100,
        flex: 0.25,
        display: 'flex',
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          if (row.spec_name === 'Default' || row.is_base_config) return null;
          return (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setConfirmTitle(
                  t(StringKeys.DELETE_CONFIGURATION, {
                    configuration: row.spec_name,
                  }),
                );
                setConfirmMessage(
                  t(
                    StringKeys.ARE_YOU_SURE_YOU_WOULD_LIKE_TO_DELETE_CONFIGURATION,
                    { configuration: row.spec_name },
                  ),
                );
                setConfigurationToRemove(row);
                setShowConfirmModal(true);
              }}
              sx={{
                backgroundColor: COLORS.PINK,
                borderRadius: '10px',
                width: 40,
                height: 40,
                '&:hover': { backgroundColor: COLORS.HOVER_RED },
              }}
            >
              <Delete sx={{ color: COLORS.DARK_RED }} />
            </IconButton>
          );
        },
      },
    ],
    [
      t,
      translateConfigValue,
      editConfiguration,
      showConfigurationToEditModal,
      setConfirmTitle,
      setConfirmMessage,
      setConfigurationToRemove,
      setShowConfirmModal,
      handleCloseSensitivityModal,
    ],
  );

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
    >
      <PageHeader
        action={() => setShowCreateConfigurationModal()}
        icon={rowsLocked ? <Visibility /> : <Add />}
        header={t(StringKeys.COMPASS_CONFIGURATION)}
        label={t(rowsLocked ? StringKeys.VIEW_SAMPLE_CONFIGURATION : StringKeys.ADD_NEW)}
        disabled={false}
        headerColor={COLORS.SAFEGUARD_BLUE}
      />
      {!isMobile ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            width: '100%',
            minHeight: 0,
          }}
        >
          <NavigatorDataGrid
            rows={sortedConfigurations}
            columns={columns}
            getRowId={(row) => row.id}
            getRowClassName={(p) => {
              const classes: string[] = [];
              if (p.row.spec_name === configToHighlight) classes.push('highlight-row');
              if (isRowEntitlementLocked(rowsLocked, Boolean(p.row.is_base_config))) {
                classes.push(ENTITLEMENT_LOCKED_ROW_CLASS);
              }
              return classes.join(' ');
            }}
            initialPageSize={15}
            pageSizeOptions={[15, 25, 50]}
            showToolbar={false}
            height="100%"
            sx={{
              '& .MuiDataGrid-columnHeader': {
                backgroundColor: COLORS.SAFEGUARD_BLUE,
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                color: COLORS.WHITE,
                fontWeight: 'bold',
              },
              '& .MuiDataGrid-row.highlight-row': {
                backgroundColor: COLORS.LIGHT_GREEN,
              },
              '& .MuiDataGrid-row': {
                '&:hover': { backgroundColor: COLORS.TABLE_HEADER_GRAY },
              },
              ...entitlementLockedRowSx,
            }}
          />
        </Box>
      ) : (
        <Box sx={{ width: '100%', mt: 2, pb: 5 }}>
          <Grid container spacing={2}>
            {sortedConfigurations.map((c) => {
              return (
                <Grid key={c.id} size={12}>
                  <Paper
                    elevation={2}
                    sx={{
                      borderRadius: '12px',
                      overflow: 'hidden',
                      backgroundColor:
                        c.spec_name === configToHighlight
                          ? COLORS.LIGHT_GREEN
                          : COLORS.WHITE,
                      ...(isRowEntitlementLocked(rowsLocked, Boolean(c.is_base_config))
                        ? entitlementLockedCardSx
                        : {}),
                    }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: COLORS.SAFEGUARD_BLUE,
                        color: 'white',
                      }}
                    >
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>
                          {c.spec_name}
                        </Typography>
                        {c.is_base_config && (
                          <LightTooltip
                            title={t(StringKeys.BASE_CONFIG_LOCKED_INFO)}
                          >
                            <IconButton
                              size="small"
                              onClick={(e) => e.stopPropagation()}
                              aria-label={t(StringKeys.BASE_CONFIG_LOCKED_INFO)}
                              sx={{ p: 0.25 }}
                            >
                              <LockOutlined
                                sx={{ fontSize: 16, color: COLORS.RED }}
                              />
                            </IconButton>
                          </LightTooltip>
                        )}
                      </Box>
                      {!c.is_base_config && (
                        <>
                          <IconButton
                            onClick={(e) =>
                              setMenuState({
                                anchorEl: e.currentTarget,
                                activeSpec: c.spec_name,
                              })
                            }
                            sx={{ color: 'white' }}
                          >
                            <MoreVert />
                          </IconButton>
                          <Menu
                            anchorEl={menuState.anchorEl}
                            open={menuState.activeSpec === c.spec_name}
                            onClose={() =>
                              setMenuState({ anchorEl: null, activeSpec: null })
                            }
                          >
                            <MenuItem
                              onClick={() => {
                                editConfiguration(c);
                                showConfigurationToEditModal();
                                setMenuState({
                                  anchorEl: null,
                                  activeSpec: null,
                                });
                              }}
                            >
                              <ListItemIcon>
                                <Edit />
                              </ListItemIcon>
                              {t(StringKeys.EDIT)}
                            </MenuItem>
                            {c.spec_name !== 'Default' && (
                              <MenuItem
                                onClick={() => {
                                  setConfirmTitle(
                                    t(StringKeys.DELETE_CONFIGURATION, {
                                      configuration: c.spec_name,
                                    }),
                                  );
                                  setConfirmMessage(
                                    t(
                                      StringKeys.ARE_YOU_SURE_YOU_WOULD_LIKE_TO_DELETE_CONFIGURATION,
                                      { configuration: c.spec_name },
                                    ),
                                  );
                                  setConfigurationToRemove(c);
                                  setShowConfirmModal(true);
                                  setMenuState({
                                    anchorEl: null,
                                    activeSpec: null,
                                  });
                                }}
                              >
                                <ListItemIcon>
                                  <Delete />
                                </ListItemIcon>
                                {t(StringKeys.DELETE)}
                              </MenuItem>
                            )}
                          </Menu>
                        </>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}
      {sensitivityModal.show &&
        sensitivityModal.config &&
        sensitivityModal.type && (
          <PvdSensitivityQuickViewModal
            show={sensitivityModal.show}
            onHide={handleCloseSensitivityModal}
            config={sensitivityModal.config}
            sensitivityType={sensitivityModal.type}
          />
        )}
    </Box>
  );
};

export default PvdConfigTable;
